import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import MyServices from "../own/components/MyServices";
import MyExperience from "../own/components/MyExperience";
import MyImages from "../own/components/MyImages";
import MyReviews from "../own/components/MyReviews";
import { freelancerCoverUpload } from "../../../services/freelancerService";
import { editUserProfile } from "../../../services/userService";
import { formatPhone } from "../../../utils/formatPhone";
import { getImage } from "../../../utils/cloudinarySizing";

// UserOwnProfile.js
const UserOwnProfile = ({ reviews, freelancerData, setFreelancerData, handleUpload, setModalOpen, isModalOpen, handleUpdate}) => {
    const [activeComponent, setActiveComponent] = useState("Prices / Subscriptions");
    const [sliderIndex, setSliderIndex] = useState(0);
    const [isOverlayOpen, setOverlayOpen] = useState(false);
    const [showContactInfo, setShowContactInfo] = useState(false);
    const [savingContact, setSavingContact] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    const contactRef = useRef(null);
    const { id, section } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [brokenProfileImage, setBrokenProfileImage] = useState(false);

    const profileImageSrc =
        !brokenProfileImage && freelancerData?.photo_original
            ? getImage(freelancerData.photo_original, "avatar_sm")
            : "/servspace-placeholder-500x500.png";

    const handleOverlayToggle = () => {
        setOverlayOpen(!isOverlayOpen);
    };

    const overlayImageSrc =
        !brokenProfileImage && freelancerData?.photo_original
            ? freelancerData.photo_original
            : "/servspace-placeholder-500x500.png";

    const tabs = [
        { name: "Services", icon: "fa-solid fa-wrench" },
        { name: "Experience", icon: "fa-solid fa-hourglass-half" },
        { name: "Images", icon: "fa-solid fa-image" }, // Simplified this entry
        { name: "Reviews", icon: "fa-solid fa-star", style: { color: "#ffcc00" } },
    ];

    const [contactForm, setContactForm] = useState({
        contact_email: "",
        phone_number: "",
    });

    // Sync tab with URL
    useEffect(() => {
        const current = location.pathname.split("/").pop();
        const match = tabs.findIndex(
            (tab) => tab.name.toLowerCase() === current.toLowerCase()
        );

        if (match !== -1) {
            setActiveComponent(tabs[match].name);
            setSliderIndex(match);
        } else {
            // default tab
            setActiveComponent("Services");
            setSliderIndex(0);
        }
    }, [location.pathname]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 900);
        handleResize(); // initialize
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const renderComponent = () => {
        switch (activeComponent) {
            case "Services":
                return <MyServices />;
            case 'Experience':
                return <MyExperience freelancerData={freelancerData} />;
            case 'Images':
                return <MyImages freelancerId={freelancerData.id} />;
            case 'Reviews':
                return <MyReviews
                    freelancerId={freelancerData.id} />;
            default:
                return null;
        }
    };

    const renderTabButtons = () => (
        tabs.map((tab, index) => (
            <button
                key={tab.name}
                className={`nav-button ${activeComponent === tab.name ? "active" : ""}`}
                onClick={() => handleTabClick(tab.name, index)}
            >
                {tab.icon && <i className={tab.icon} style={tab.style}></i>}
                <span style={{ marginLeft: "8px" }}>
                    {tab.name} {/* Directly render tab.name and remove the conditional */}
                </span>
            </button>
        ))
    );


    const handleTabClick = (tabName, index) => {
        setActiveComponent(tabName);
        setSliderIndex(index);
        navigate(`/freelancer/${id}/${tabName.toLowerCase()}`);
    };

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("cover_image", file);

        try {
            const response = await freelancerCoverUpload(formData);

            alert(response.data.message); // Notify user of success

            // Update the local state to reflect the new image
            setFreelancerData(prev => ({
                ...prev,
                cover_image: URL.createObjectURL(file), // make sure this matches API response key
            }));
        } catch (error) {
            console.error("Error uploading cover photo:", error);
            alert("Failed to upload cover photo. Please try again.");
        }
    };

    // Calculate average rating
    const calculateAverageRating = (reviews) => {
        if (reviews.length === 0) return 0; // Avoid division by zero
        const totalRating = reviews.reduce((acc, review) => acc + review.rating, 0);
        return (totalRating / reviews.length).toFixed(1); // Return rounded value
    };

    const averageRating = calculateAverageRating(reviews);

    const formatLocation = (city, state) => {
        if (!city && !state) return null;
        if (city && state) return ` ${city}, ${state}`;
        return city || state;
    };

    const handleSaveContactInfo = async () => {
        setSavingContact(true);

        try {
            const formData = new FormData();

            if (contactForm.contact_email !== freelancerData.contact_email) {
                formData.append("contact_email", contactForm.contact_email);
            }

            if (contactForm.phone_number !== freelancerData.phone_number) {
                formData.append("phone_number", contactForm.phone_number.replace(/\D/g, ""));
            }

            if ([...formData.keys()].length === 0) {
                setShowContactInfo(false);
                return;
            }

            const updatedProfile = await editUserProfile(formData);

            // ✅ Optimistically update local state
            setFreelancerData((prev) => ({
                ...prev,
                profile: {
                    ...prev.profile,
                    contact_email: updatedProfile.contact_email,
                    phone_number: updatedProfile.phone_number,
                },
            }));

            setShowContactInfo(false);
        } catch (err) {
            console.error("Failed to update contact info:", err);
        } finally {
            setSavingContact(false);
        }
    };

    const handleCancelContactEdit = () => {
        setContactForm({
            contact_email: freelancerData.contact_email || "",
            phone_number: freelancerData.phone_number || "",
        });
        setShowContactInfo(false);
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (contactRef.current && !contactRef.current.contains(e.target)) {
                setShowContactInfo(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (showContactInfo && freelancerData) {
            setContactForm({
                contact_email: freelancerData.contact_email || "",
                phone_number: freelancerData.phone_number || "",
            });
        }
    }, [showContactInfo, freelancerData]);

    return (
        <div className="freelancer-page-wrapper">
            <div className="freelancer-profile-container">
                <div className='centered-content'>
                    <div className="freelancer-profile-content">
                        <div className="photo-and-name">
                            <div className="freelancer-profile-photo">
                                <img
                                    onClick={handleOverlayToggle}
                                    src={profileImageSrc}
                                    alt="Profile"
                                    onError={() => setBrokenProfileImage(true)}
                                />
                                <div className="edit-profile-photo" onClick={() => document.getElementById('file-input').click()}>
                                    <input type="file" onChange={handleUpload} style={{ display: 'none' }} id="file-input" />
                                    <label className="camera-icon">
                                        <i className="fa-solid fa-camera"></i>
                                    </label>
                                </div>

                                {/* Overlay for Enlarged Image */}
                                {isOverlayOpen && (
                                    <div className="image-overlay" onClick={handleOverlayToggle}>
                                        {freelancerData ? (
                                            <img src={overlayImageSrc} alt="Enlarged Profile" />
                                        ) : (
                                            <p>Loading...</p>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="name-icons-wrapper">
                                <div className="name-row">
                                    <h3 className="freelancer-name">
                                        {`${freelancerData.first_name || ""} ${freelancerData.last_name || ""}`.trim() || "Freelancer"}
                                    </h3>
                                    <span className="freelancer-handle">@{freelancerData.username}</span>
                                </div>

                                <div className="freelancer-icons">

                                    {/* Contact button */}
                                    <div className="contact-button-container" ref={contactRef}>
                                        <button
                                            className="contact-button"
                                            onClick={() => setShowContactInfo((prev) => !prev)}
                                        >
                                            Contact Info
                                            <i className={`fa-solid fa-chevron-${showContactInfo ? "up" : "down"}`} />
                                        </button>

                                        {showContactInfo && (
                                            <div className="contact-dropdown">
                                                <input
                                                    type="email"
                                                    placeholder="Contact email"
                                                    value={contactForm.contact_email}
                                                    onChange={(e) =>
                                                        setContactForm({ ...contactForm, contact_email: e.target.value })
                                                    }
                                                />

                                                <input
                                                    type="text"
                                                    placeholder="Phone number"
                                                    value={formatPhone(contactForm.phone_number)}
                                                    onChange={(e) =>
                                                        setContactForm({ ...contactForm, phone_number: e.target.value })
                                                    }
                                                />

                                                <div className="contact-actions">
                                                    <button
                                                        className="contact-save-btn"
                                                        onClick={handleSaveContactInfo}
                                                        disabled={savingContact}
                                                    >
                                                        {savingContact ? "Saving..." : "Save"}
                                                    </button>

                                                    <button
                                                        className="contact-cancel-btn"
                                                        onClick={handleCancelContactEdit}
                                                        disabled={savingContact}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Location group */}
                                    {(freelancerData.city || freelancerData.state) && (
                                        <div className="freelancer-location">
                                            <i className="fa-solid fa-location-dot"></i>
                                            <span>
                                                {formatLocation(freelancerData.city, freelancerData.state)}
                                            </span>
                                        </div>
                                    )}

                                    {/* Divider — only show if location AND at least one link exists */}
                                    {(freelancerData.city || freelancerData.state) &&
                                        (freelancerData.portfolio_link ||
                                            freelancerData.facebook ||
                                            freelancerData.instagram ||
                                            freelancerData.x ||
                                            freelancerData.youtube) && (
                                            <span className="freelancer-divider">|</span>
                                        )}

                                    <div className="own-freelancer-links-row">
                                        {freelancerData.portfolio_link && (
                                            <a
                                                href={freelancerData.portfolio_link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="freelancer-portfolio-icon"
                                                title="Website"
                                            >
                                                <i className="fa-solid fa-link"></i>
                                            </a>
                                        )}

                                        {freelancerData.facebook && (
                                            <a
                                                href={freelancerData.facebook}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="freelancer-portfolio-icon"
                                                title="Facebook"
                                            >
                                                <i className="fa-brands fa-facebook"></i>
                                            </a>
                                        )}

                                        {freelancerData.instagram && (
                                            <a
                                                href={freelancerData.instagram}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="freelancer-portfolio-icon"
                                                title="Instagram"
                                            >
                                                <i className="fa-brands fa-instagram"></i>
                                            </a>
                                        )}

                                        {freelancerData.x && (
                                            <a
                                                href={freelancerData.x}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="freelancer-portfolio-icon"
                                                title="X"
                                            >
                                                <i className="fa-brands fa-x-twitter"></i>
                                            </a>
                                        )}

                                        {freelancerData.youtube && (
                                            <a
                                                href={freelancerData.youtube}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="freelancer-portfolio-icon"
                                                title="YouTube"
                                            >
                                                <i className="fa-brands fa-youtube"></i>
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="profile-stats-buttons">
                            <div className="stats-container">
                                <div className="stat-item">
                                    <p><i className="fa-solid fa-star"></i> {averageRating}</p>
                                    <h4>Rating</h4>
                                </div>
                            </div>
                            {isMobile && (<div className="freelancer-buttons">
                                <button className="mobile-edit-button" onClick={() => setModalOpen(true)}>
                                    <i className="fa-solid fa-pen"></i> Edit
                                </button>
                            </div>)}
                            {!isMobile && (<div className="freelancer-buttons">
                                <button className="edit-button" onClick={() => setModalOpen(true)}>
                                    <i className="fa-solid fa-pen"></i>
                                </button>
                            </div>)}
                        </div>
                    </div>
                    <div className="container">
                        <div className="button-row">
                            {/* Sliding Bar */}
                            <div
                                className="freelancer-slider"
                                style={{
                                    width: `calc(100% / ${tabs.length})`,
                                    transform: `translateX(${sliderIndex * 100}%)`,
                                }}
                            />
                            {renderTabButtons()}
                        </div>
                        {renderComponent()}
                    </div>
                </div>
            </div>
        </div>

    );
};

export default UserOwnProfile;
